import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/LoadingScreen';
import { useAuth } from '@/app/AuthContext';
import { BRANDING_QUERY_KEY, announceBrandingChange } from '@/app/useCompanyBackground';
import { ApiRequestError } from '@/services/api';
import { brandingService, type Branding } from '@/services/branding.service';
import { Permission } from '@/types/permission';
import { v } from '@/theme/tokens';

const ACCEPT = 'image/jpeg,image/png,image/webp';

function ButtonSpinner() {
  return (
    <span
      aria-hidden
      className="rt-spinner"
      style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        animation: 'spin 0.8s linear infinite',
      }}
    />
  );
}

/**
 * Настройки → Фон приложения. The owner alone sees this section
 * (Permission.MANAGE_BRAND); the photo it sets is what every signed-in user
 * sees under the glass. The server checks the right again on each change and
 * re-encodes the upload, so what is stored never carries the phone's metadata.
 */
export function BackgroundSection() {
  const { can } = useAuth();
  const client = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const branding = useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: ({ signal }) => brandingService.get(signal),
    staleTime: 5 * 60_000,
  });

  const settle = (message: string) => (next: Branding) => {
    client.setQueryData(BRANDING_QUERY_KEY, next);
    announceBrandingChange();
    setError(null);
    setDone(message);
  };
  const fail = (fallback: string) => (e: unknown) => {
    setDone(null);
    setError(e instanceof ApiRequestError ? e.message : fallback);
  };

  const upload = useMutation({
    mutationFn: brandingService.uploadBackground,
    onSuccess: settle('Фон обновлён. Остальные увидят его в течение минуты.'),
    onError: fail('Не удалось загрузить фото. Проверьте соединение и попробуйте ещё раз.'),
  });
  const reset = useMutation({
    mutationFn: brandingService.resetBackground,
    onSuccess: settle('Вернули стандартный фон.'),
    onError: fail('Не удалось вернуть стандартный фон. Попробуйте ещё раз.'),
  });

  if (!can(Permission.MANAGE_BRAND)) return null;

  const data = branding.data;
  const busy = upload.isPending || reset.isPending;
  const classic = document.documentElement.dataset.skin === 'classic';

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // The same file may be chosen again after an error.
    event.target.value = '';
    if (!file) return;
    setDone(null);
    setError(null);
    upload.mutate(file);
  };

  return (
    <Card>
      <SectionTitle
        right={
          data ? (
            <Badge tone={data.custom ? 'accent' : 'neutral'}>
              {data.custom ? 'Своё фото' : 'Стандартный'}
            </Badge>
          ) : undefined
        }
      >
        Фон приложения
      </SectionTitle>

      {branding.isPending && <Spinner />}

      {data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div
            role="img"
            aria-label={data.custom ? 'Текущий фон: своё фото' : 'Текущий фон: стандартное фото Ramen Ten'}
            style={{
              aspectRatio: '16 / 10',
              borderRadius: 14,
              border: `1px solid ${v('line')}`,
              backgroundColor: v('panel'),
              backgroundImage: `url("${brandingService.backgroundUrl(data)}")`,
              backgroundSize: 'cover',
              backgroundPosition: '58% 40%',
              opacity: upload.isPending ? 0.6 : 1,
              transition: 'opacity 0.2s ease',
            }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <p style={{ fontSize: 13, color: v('ink-sub'), margin: 0, lineHeight: 1.5 }}>
              Фото лежит под стеклом на всех экранах и одинаково для всех пользователей компании.
              Менять его можете только вы. У тех, у кого приложение уже открыто, новый фон появится
              в течение минуты или сразу, когда они вернутся во вкладку. Читаемость цифр приложение
              держит само: фото приглушается и размывается под карточками.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <input
                ref={input}
                type="file"
                accept={ACCEPT}
                onChange={onFile}
                aria-label="Фото для фона"
                // The button below is the control; the picker only opens from it.
                hidden
              />
              <Button
                variant="primary"
                onClick={() => input.current?.click()}
                disabled={reset.isPending}
                aria-busy={upload.isPending}
              >
                {upload.isPending && <ButtonSpinner />}
                {upload.isPending ? 'Загружаем фото…' : 'Загрузить фото'}
              </Button>
              <Button
                onClick={() => reset.mutate()}
                disabled={!data.custom || upload.isPending}
                aria-busy={reset.isPending}
              >
                {reset.isPending && <ButtonSpinner />}
                Вернуть стандартный
              </Button>
            </div>

            <div style={{ fontSize: 12, color: v('ink-muted'), lineHeight: 1.5 }}>
              JPEG, PNG или WebP до 15 МБ, длинная сторона от 1000 px. Подойдёт горизонтальное фото.
              Геолокация и другие данные снимка удаляются при загрузке.
            </div>

            {classic && (
              <div style={{ fontSize: 12, color: v('ink-muted') }}>
                Сейчас у вас включено классическое оформление: фон виден только в стеклянном.
              </div>
            )}

            <div aria-live="polite" style={{ fontSize: 12.5 }}>
              {done && !busy && <span style={{ color: v('positive') }}>{done}</span>}
            </div>
            {error && (
              <div role="alert" style={{ color: v('negative'), fontSize: 12.5 }}>
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
